import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useOrgStore } from '@/stores/org-store';
import { agentConversationsQueryPrefix } from '@/hooks/queries/agents/use-agent-conversations';
import {
  renameAgentConversation,
  type AgentConversationSummary,
} from '@/services/agents/config';

interface RenameConversationInput {
  conversationId: string;
  title: string;
}

interface PendingRename {
  savedTitle: string | undefined;
  inFlight: number;
}

export const useRenameAgentConversation = (agentId: string | undefined) => {
  const queryClient = useQueryClient();
  const { currentOrg } = useOrgStore();
  const orgId = currentOrg?.orgid;
  const lastRename = useRef<Promise<unknown>>(Promise.resolve());
  const pendingRenames = useRef(new Map<string, PendingRename>());

  const queryKey = agentConversationsQueryPrefix(orgId, agentId);

  const cachedTitle = (conversationId: string) => {
    for (const [, conversations] of queryClient.getQueriesData<
      AgentConversationSummary[]
    >({ queryKey })) {
      const match = conversations?.find(
        (conversation) => conversation.conversation_id === conversationId,
      );
      if (match) return match.title;
    }
    return undefined;
  };

  const writeTitle = (conversationId: string, title: string | undefined) => {
    queryClient.setQueriesData<AgentConversationSummary[]>(
      { queryKey },
      (conversations) =>
        conversations?.some(
          (conversation) =>
            conversation.conversation_id === conversationId &&
            conversation.title !== title,
        )
          ? conversations.map((conversation) =>
              conversation.conversation_id === conversationId
                ? { ...conversation, title }
                : conversation,
            )
          : conversations,
    );
  };

  return useMutation<void, Error, RenameConversationInput>({
    // The app retries mutations once by default, which re-sends a failed
    // rename a second later — after a newer one has landed, leaving the older
    // title on the server. Surfacing the failure beats resending it blind.
    retry: 0,
    mutationFn: ({ conversationId, title }) => {
      // Requests from one card go out in submission order — a retried rename
      // landing after a newer one would resurrect the older title on the
      // server. The optimistic update in onMutate is not held up by this.
      const rename = lastRename.current
        .catch(() => {})
        .then(() =>
          renameAgentConversation(orgId!, agentId!, conversationId, title),
        );
      lastRename.current = rename;
      return rename;
    },
    onMutate: async ({ conversationId, title }) => {
      await queryClient.cancelQueries({ queryKey });

      const pending = pendingRenames.current.get(conversationId);
      if (pending) {
        pending.inFlight += 1;
      } else {
        // The title to fall back to is the one the server last accepted, not
        // whatever is on screen — that may be an earlier optimistic rename
        // still in flight, which a failure must not leave behind.
        pendingRenames.current.set(conversationId, {
          savedTitle: cachedTitle(conversationId),
          inFlight: 1,
        });
      }

      writeTitle(conversationId, title);
    },
    onSuccess: (_data, { conversationId, title }) => {
      const pending = pendingRenames.current.get(conversationId);
      if (pending) pending.savedTitle = title;
    },
    onError: (error, { conversationId }) => {
      Sentry.captureException(error, {
        extra: {
          context: 'rename-agent-conversation',
          orgId,
          agentId,
          conversationId,
        },
      });
    },
    onSettled: (_data, _error, { conversationId }) => {
      const pending = pendingRenames.current.get(conversationId);
      if (pending) {
        pending.inFlight -= 1;
        // Only the last rename of this chat reconciles, so an earlier failure
        // cannot roll a newer title off the card while it is still in flight.
        if (pending.inFlight === 0) {
          pendingRenames.current.delete(conversationId);
          writeTitle(conversationId, pending.savedTitle);
        }
      }

      // Marked stale rather than refetched: the cache already holds the title
      // we sent, an immediate refetch would overwrite a concurrent rename, and
      // after a network failure it would fail too and blank the list.
      queryClient.invalidateQueries({ queryKey, refetchType: 'none' });
    },
  });
};
